import { useEffect, useId, useState, type FormEvent } from "react";
import { Link, useParams } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { isNotFound } from "../../api/errors";
import { useDeployService, useDeployments, useService, useUpdateService } from "../../api/queries";
import type { Deployment } from "../../api/types";
import { EmptyState, ErrorState, LoadingState } from "../../components/AsyncStates";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { QueryStates } from "../../components/QueryStates";
import { DeploymentStatusPill, ServiceStatusPill } from "../../components/StatePill";
import { useFleet } from "../../fleet/FleetContext";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";
import "./services.css";

const DEPLOYMENT_COLUMNS: DataTableColumn<Deployment>[] = [
  { key: "status", header: "Status", cell: (dep) => <DeploymentStatusPill status={dep.status} /> },
  { key: "targetCount", header: "Target count", cell: (dep) => dep.targetCount, className: "data-table__mono" },
  { key: "createdBy", header: "Triggered by", cell: (dep) => dep.createdBy || "—", className: "data-table__secondary" },
  {
    key: "createdAt",
    header: "Started",
    cell: (dep) => new Date(dep.createdAt).toLocaleString(),
    className: "data-table__secondary",
  },
  {
    key: "detail",
    header: "Detail",
    cell: (dep) => dep.detail ?? "—",
    className: (dep) => (dep.detail ? "data-table__secondary" : "data-table__empty-cell"),
  },
];

export function ServiceDetailPage() {
  const { projectId, serviceId } = useParams<{ projectId: string; serviceId: string }>();
  const { currentId } = useFleet();
  const { session } = useAuth();
  const { pushToast } = useToast();

  const { data: service, isPending, isError, error, refetch } = useService(currentId, projectId ?? "", serviceId);
  const {
    data: deploymentsPage,
    isPending: deploymentsPending,
    isError: deploymentsIsError,
    error: deploymentsError,
    refetch: refetchDeployments,
  } = useDeployments(currentId, projectId ?? "", serviceId ?? "");
  const updateMutation = useUpdateService(currentId, projectId ?? "");
  const deployMutation = useDeployService(currentId, projectId ?? "");

  const imageId = useId();
  const desiredCountId = useId();
  const [image, setImage] = useState("");
  const [desiredCount, setDesiredCount] = useState("");

  // Seeds the form from the loaded service and re-seeds whenever the
  // service *id* changes — an operator's in-progress edit is never silently
  // clobbered by a background refetch.
  useEffect(() => {
    if (!service) return;
    setImage(service.image ?? "");
    setDesiredCount(String(service.desiredCount));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.id]);

  // Mirrors the server's authz matrix (service:update is granted to
  // operator/admin only) — this only hides the control (S-8).
  const canManage = session?.role === "admin" || session?.role === "operator";

  function handleUpdateSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!serviceId) return;
    const parsedCount = Number.parseInt(desiredCount, 10);
    updateMutation.mutate(
      {
        id: serviceId,
        input: {
          image: image.trim(),
          desiredCount: parsedCount,
        },
      },
      {
        onSuccess: () => pushToast({ title: "Configuration saved.", tone: "success" }),
        onError: (err) =>
          pushToast({
            title: "Couldn't save configuration",
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          }),
      },
    );
  }

  function handleDeploy() {
    if (!serviceId) return;
    // deployService always rejects (deploying isn't implemented server-side
    // yet — see the doc comment on ApiClient.deployService); the error is
    // rendered as a persistent banner below, never a toast that fades
    // before the operator has registered it, and never a fake success.
    deployMutation.mutate({ id: serviceId });
  }

  if (isPending) {
    return <LoadingState label="Loading service…" />;
  }

  if (isError) {
    if (isNotFound(error)) {
      return (
        <ErrorState
          title="Service not found"
          description="It may have been removed, or the link is out of date. Head back to the project."
        />
      );
    }
    return (
      <ErrorState
        description={error instanceof Error ? error.message : "Couldn't load this service."}
        onRetry={refetch}
      />
    );
  }

  if (!service) return null;

  const parsedCount = Number.parseInt(desiredCount, 10);
  const canSave =
    canManage && image.trim().length > 0 && Number.isInteger(parsedCount) && parsedCount >= 0 && !updateMutation.isPending;

  return (
    <div className="service-detail">
      <Link to={`/projects/${projectId}`} className="service-detail__back">
        ← Back to project
      </Link>

      <div className="service-detail__header">
        <div className="service-detail__title-row">
          <h1 className="service-detail__title">{service.name}</h1>
          <ServiceStatusPill status={service.status} />
        </div>
        {canManage && (
          <Button variant="primary" disabled={deployMutation.isPending} onClick={handleDeploy}>
            {deployMutation.isPending ? "Checking…" : "Deploy"}
          </Button>
        )}
      </div>

      {deployMutation.isError && (
        <Banner tone="info" title="Deploying a service isn't available yet" className="service-detail__deploy-banner">
          {deployMutation.error instanceof Error
            ? deployMutation.error.message
            : "This capability hasn't shipped yet."}{" "}
          Define your service here; to run it today, deploy it as a{" "}
          <Link to="/stacks">Stack</Link> instead.
        </Banner>
      )}

      <section className="service-detail__section" aria-labelledby="service-configure-heading">
        <h2 id="service-configure-heading">Configure</h2>
        <form onSubmit={handleUpdateSubmit} noValidate>
          <label className="form-field" htmlFor={imageId}>
            <span>Image</span>
            <input
              id={imageId}
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={image}
              onChange={(e) => setImage(e.target.value)}
              disabled={!canManage}
              required
            />
            <span className="form-field__hint">
              Saving here doesn't deploy it — deploying a service isn't available yet (see above).
            </span>
          </label>

          <label className="form-field" htmlFor={desiredCountId}>
            <span>Desired count</span>
            <input
              id={desiredCountId}
              type="number"
              min={0}
              value={desiredCount}
              onChange={(e) => setDesiredCount(e.target.value)}
              disabled={!canManage}
              required
            />
          </label>

          {canManage && (
            <div className="form-actions">
              <Button type="submit" variant="secondary" disabled={!canSave}>
                {updateMutation.isPending ? "Saving…" : "Save configuration"}
              </Button>
            </div>
          )}
        </form>
      </section>

      <section className="service-detail__section" aria-labelledby="service-history-heading">
        <h2 id="service-history-heading">Deployment history</h2>
        <QueryStates
          isLoading={deploymentsPending}
          loadingLabel="Loading deployment history…"
          isError={deploymentsIsError}
          error={deploymentsError}
          onRetry={refetchDeployments}
          errorDescription={
            deploymentsError instanceof Error
              ? deploymentsError.message
              : "Couldn't load deployment history. Check your connection and try again."
          }
          isEmpty={Boolean(deploymentsPage && deploymentsPage.items.length === 0)}
          empty={
            <EmptyState
              title="No deployments yet"
              description="Deploying a service isn't available yet, so this stays empty — see the notice above."
            />
          }
        >
          {deploymentsPage && (
            <DataTable columns={DEPLOYMENT_COLUMNS} rows={deploymentsPage.items} rowKey={(dep) => dep.id} />
          )}
        </QueryStates>
      </section>
    </div>
  );
}
